import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuditOpinion } from '../../../models/auditOpinion';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-opinion-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-opinion-list.component.html',
  styleUrl: './audit-opinion-list.component.scss'
})
export class AuditOpinionListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditOpinions = signal<AuditOpinion[]>([]);
  rightItem: AuditOpinion | undefined = undefined;
  matchedItems: AuditOpinion[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditOpinions();
  }

  loadAuditOpinions(): void {
    this.spinnerService.show();
    this.lutService.getAuditOpinions().subscribe({
      next: (data) => {
        const sortedData = this.sortAuditOpinions(data);
        this.auditOpinions.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading audit opinions:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortAuditOpinions(auditOpinions: AuditOpinion[]): AuditOpinion[] {
    return [...auditOpinions].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateAuditOpinion(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(auditOpinion: AuditOpinion): void {
  }

  onEditAuditOpinion(auditOpinion: AuditOpinion): void {
  }

  onDeleteAuditOpinion(auditOpinion: AuditOpinion): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditOpinion.name)
      .subscribe(result => {
        if (result) {
          const originalAuditOpinions = this.auditOpinions();

          this.auditOpinions.update(auditOpinions => auditOpinions.filter(ao => ao.id !== auditOpinion.id));

          this.spinnerService.show();
          this.lutService.deleteAuditOpinion(auditOpinion.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit opinion deleted successfully');
            },
            error: (error) => {
              console.error('Error deleting audit opinion:', error);
              this.spinnerService.hide();
              this.auditOpinions.set(originalAuditOpinions);
              this.toast.error(`<strong>Failed to delete Audit Opinion:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditOpinion, newName: string} | {item: AuditOpinion, newValue: string, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: AuditOpinion;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    this.auditOpinions.update(auditOpinions => {
      const updated = auditOpinions.map(ao => ao.id === event.item.id ? updatedItem : ao);
      return this.sortAuditOpinions(updated);
    });

    this.spinnerService.show();
    this.lutService.updateAuditOpinion(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit opinion updated successfully');
        this.auditOpinions.update(auditOpinions => {
          const updated = auditOpinions.map(ao => ao.id === event.item.id ? response : ao);
          return this.sortAuditOpinions(updated);
        });
      },
      error: (error) => {
        console.error('Error updating audit opinion:', error);
        this.spinnerService.hide();
        this.auditOpinions.update(auditOpinions =>
          auditOpinions.map(ao => ao.id === event.item.id ? originalItem : ao)
        );
        this.toast.error(`<strong>Failed to update Audit Opinion:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const newAuditOpinion: AuditOpinion = { id: tempId, name: event['name'], systemReserved: false };

    this.auditOpinions.update(auditOpinions => this.sortAuditOpinions([...auditOpinions, newAuditOpinion]));

    this.spinnerService.show();
    this.lutService.createAuditOpinion({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit opinion created successfully');
        this.auditOpinions.update(auditOpinions => {
          const updated = auditOpinions.map(ao => ao.id === tempId ? response : ao);
          return this.sortAuditOpinions(updated);
        });
      },
      error: (error) => {
        console.error('Error creating audit opinion:', error);
        this.spinnerService.hide();
        this.auditOpinions.update(auditOpinions =>
          auditOpinions.filter(ao => ao.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Audit Opinion:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: AuditOpinion, matchedItems?: AuditOpinion[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onAuditOpinionCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalAuditOpinions = this.auditOpinions();

        this.auditOpinions.update(auditOpinions =>
          auditOpinions.filter(ao => !bulkMergeRequest.leftIds.includes(ao.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeAuditOpinionsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Audit opinions merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadAuditOpinions();
          },
          error: (error) => {
            console.error('Error merging audit opinions:', error);
            this.spinnerService.hide();
            this.auditOpinions.set(originalAuditOpinions);
            this.rightItem = originalAuditOpinions.find(ao => ao.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAuditOpinions.filter(ao => bulkMergeRequest.leftIds.includes(ao.id));
            this.toast.error(`<strong>Failed to merge Audit Opinions:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
